import { useState } from 'react';
import api from '../services/api';
import { AVATAR_PALETTE } from '../utils/avatarColors';

// Create or edit a group, including its identity: name, type, an emoji icon, and
// a color (from the shared brand palette). icon/color are optional — null means
// the chip falls back to a deterministic color + no emoji.

const GROUP_TYPES = [
    { value: 'family', label: 'Family' },
    { value: 'friends', label: 'Friends' },
    { value: 'work', label: 'Work' },
    { value: 'secret_santa', label: 'Secret Santa' },
    { value: 'book_club', label: 'Book Club' },
    { value: 'custom', label: 'Other…' },
];
const PRESET_VALUES = GROUP_TYPES.map((t) => t.value);
const QUICK_EMOJI = ['🎄', '🎁', '🎂', '🏠', '🏢', '🎉', '❤️', '🐾', '🎓', '⚽'];

export default function GroupFormModal({ group, onClose, onSaved }) {
    const isEdit = !!group;
    const typeIsPreset = group ? PRESET_VALUES.includes(group.type) : true;

    const [name, setName] = useState(group?.name || '');
    const [type, setType] = useState(group ? (typeIsPreset ? group.type : 'custom') : 'family');
    const [customType, setCustomType] = useState(group && !typeIsPreset ? group.type : '');
    const [icon, setIcon] = useState(group?.icon || '');
    const [color, setColor] = useState(group?.color || null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        setError('');
        const resolvedType = type === 'custom' ? customType.trim() || 'custom' : type;
        const payload = { name, type: resolvedType, icon: icon.trim() || null, color: color || null };
        try {
            const res = isEdit
                ? await api.put(`/groups/${group.id}`, payload)
                : await api.post('/groups', payload);
            onSaved(res.data, isEdit);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save the group.');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">{isEdit ? 'Edit Group' : 'Create New Group'}</h3>
                <form onSubmit={submit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                        <input
                            type="text"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Smith Family"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Group Type</label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {GROUP_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                        {type === 'custom' && (
                            <input
                                type="text"
                                value={customType}
                                onChange={(e) => setCustomType(e.target.value)}
                                placeholder="Type a label, e.g. neighborhood"
                                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Icon (optional)</label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {QUICK_EMOJI.map((e) => (
                                <button
                                    type="button"
                                    key={e}
                                    onClick={() => setIcon(e)}
                                    className={`w-9 h-9 rounded-md text-lg ${icon === e ? 'ring-2 ring-blue-500 bg-blue-50' : 'bg-gray-100 hover:bg-gray-200'}`}
                                >
                                    {e}
                                </button>
                            ))}
                            {icon && (
                                <button type="button" onClick={() => setIcon('')} title="Clear" className="w-9 h-9 rounded-md text-sm bg-gray-100 hover:bg-gray-200">
                                    ✕
                                </button>
                            )}
                        </div>
                        <input
                            type="text"
                            value={icon}
                            maxLength={8}
                            onChange={(e) => setIcon(e.target.value)}
                            placeholder="or type / paste an emoji"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Color (optional)</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setColor(null)}
                                title="Auto"
                                className={`w-8 h-8 rounded-full border border-gray-300 text-xs text-gray-500 flex items-center justify-center ${!color ? 'ring-2 ring-gray-400' : ''}`}
                            >
                                A
                            </button>
                            {AVATAR_PALETTE.map((sw) => (
                                <button
                                    type="button"
                                    key={sw.hex}
                                    onClick={() => setColor(sw.hex)}
                                    title={sw.name}
                                    style={{ backgroundColor: sw.hex }}
                                    className={`w-8 h-8 rounded-full ${color === sw.hex ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`}
                                />
                            ))}
                        </div>
                    </div>

                    {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create Group'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
